import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import XTAMSpinner from '../components/XTAMSpinner';
import CreateSiteWizard from '../components/CreateSiteWizard';
import { Plus, Search, MapPin, Edit, Trash2, Camera, Server, CheckCircle, Video, Building2, Shield, ChevronLeft, ChevronRight, RefreshCw, Wifi, WifiOff, AlertCircle, X, Monitor, Volume2, Copy, Check, Router } from 'lucide-react';
import {
  siteMapAlignedOperational,
  deriveSitesOperationalStats,
  type MapAlignedOperational,
} from '../data/sites';
import './SitesPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

interface Site {
  id: number;
  name: string;
  address: string;
  neighborhood?: string;
  location?: string;
  geoDepartment?: string | null;
  geoMunicipality?: string | null;
  latitude?: string;
  longitude?: string;
  ipAddress?: string;
  active: boolean;
  certificate?: boolean;
  stateId?: number;
  /** Nombre desde ``sites_states`` (``state_id``). */
  stateName?: string | null;
  clientServicesId?: number;
  /** Si false, no se aplica pausa automática del stream en vivo (PRIVADO/EU). */
  streamingOnDemandEnabled?: boolean;
  serviceName?: string; // PVVC, PRIVADO, EU
  createdAt: string;
  updatedAt: string;
  devices?: {
    cameras?: any[];
    miniots?: any[];
    xtamVideos?: any[];
    buttons?: any[];
    sirens?: any[];
    routers?: any[];
  };
}

type SiteForm = {
  name: string;
  address: string;
  neighborhood: string;
  location: string;
  geoDepartment: string;
  geoMunicipality: string;
  latitude: string;
  longitude: string;
  ipAddress: string;
  active: boolean;
  certificate: boolean;
  stateId: string;
  clientServicesId: string;
};

const SitesPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('true');

  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [editingSiteForWizard, setEditingSiteForWizard] = useState<any>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<number | null>(null);
  const [form, setForm] = useState<SiteForm>({
    name: '',
    address: '',
    neighborhood: '',
    location: '',
    geoDepartment: '',
    geoMunicipality: '',
    latitude: '',
    longitude: '',
    ipAddress: '',
    active: true,
    certificate: false,
    stateId: '',
    clientServicesId: '',
  });

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Dashboard stats
  const [stats, setStats] = useState<{
    sites: { total: number; active: number; inactive: number; private: number; pvvc: number };
    cameras: { total: number; online: number; offline: number };
  } | null>(null);

  // Filtro por estado operativo del sitio (cards clickeables)
  const [cameraStatusFilter, setCameraStatusFilter] = useState<'all' | 'online' | 'partial' | 'offline'>('all');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // Estados para modales de confirmación
  const [showConfirmDeleteSite, setShowConfirmDeleteSite] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);

  // Estado para el modal de dispositivos del sitio
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [devicesModalSite, setDevicesModalSite] = useState<Site | null>(null);
  const [copiedIp, setCopiedIp] = useState<string | null>(null);

  const copyToClipboard = async (ip: string) => {
    try {
      await navigator.clipboard.writeText(ip);
      setCopiedIp(ip);
      setTimeout(() => setCopiedIp(null), 2000);
    } catch {
      console.error('Error al copiar al portapapeles');
    }
  };

  // Función para obtener conteos de dispositivos
  const getDeviceCounts = (site: Site) => {
    const d = site.devices;
    if (!d) return { cameras: 0, xtamVideos: 0, miniots: 0, routers: 0, buttons: 0, sirens: 0, total: 0 };
    const cameras = d.cameras?.length || 0;
    const xtamVideos = d.xtamVideos?.length || 0;
    const miniots = d.miniots?.length || 0;
    const routers = d.routers?.length || 0;
    const buttons = d.buttons?.length || 0;
    const sirens = d.sirens?.length || 0;
    return { cameras, xtamVideos, miniots, routers, buttons, sirens, total: cameras + xtamVideos + miniots + routers + buttons + sirens };
  };

  const openDevicesModal = (site: Site) => {
    setDevicesModalSite(site);
    setShowDevicesModal(true);
  };

  // Función para obtener el icono según el tipo de servicio
  const getSiteIcon = (serviceName?: string) => {
    switch (serviceName?.toUpperCase()) {
      case 'PVVC':
        return <Video size={20} />;
      case 'PRIVADO':
        return <Building2 size={20} />;
      case 'EU':
        return <Shield size={20} />;
      case 'XTAM MASIVOS':
      case 'MASIVOS':
        return <Server size={20} />;
      default:
        return <MapPin size={20} />;
    }
  };

  // Función para obtener la etiqueta y estilo del tipo de servicio
  const getServiceBadge = (serviceName?: string) => {
    const name = serviceName?.toUpperCase();
    switch (name) {
      case 'PVVC':
        return (
          <span className="sites-badge sites-badge-info">
            <Video size={14} />
            PVVC
          </span>
        );
      case 'PRIVADO':
        return (
          <span className="sites-badge sites-badge-warning">
            <Building2 size={14} />
            PRIVADO
          </span>
        );
      case 'EU':
        return (
          <span className="sites-badge sites-badge-primary">
            <Shield size={14} />
            EU
          </span>
        );
      case 'XTAM MASIVOS':
      case 'MASIVOS':
        return (
          <span className="sites-badge sites-badge-info">
            <Server size={14} />
            XTAM MASIVOS
          </span>
        );
      default:
        return (
          <span className="sites-badge sites-badge-inactive">
            <MapPin size={14} />
            Sin definir
          </span>
        );
    }
  };

  const getSiteStateBadge = (site: Site) => {
    const cams = site.devices?.cameras ?? [];
    const aligned: MapAlignedOperational = siteMapAlignedOperational({
      stateId: site.stateId,
      cameras: cams as ReadonlyArray<{ state?: number | null }>,
    });
    const rawName = site.stateName?.trim();
    const id = site.stateId;

    type Mod = 'on' | 'issue' | 'maint' | 'off' | 'unknown';
    let mod: Mod;
    let label: string;
    if (aligned === 'on') {
      mod = 'on';
      label = 'ON';
    } else if (aligned === 'issue') {
      mod = 'issue';
      label = 'Con novedad';
    } else if (aligned === 'maint') {
      mod = 'maint';
      label = 'Mantenimiento';
    } else if (aligned === 'off') {
      mod = 'off';
      label = 'OFF';
    } else if (rawName) {
      mod = 'unknown';
      label = rawName;
    } else {
      return (
        <span
          className="sites-state-sema sites-state-sema--unknown"
          title="Sin state_id en sites o sin fila en sites_states"
        >
          <span className="sites-state-sema__dot" aria-hidden />
          <span>Sin dato</span>
        </span>
      );
    }

    const titleParts: string[] = [];
    if (rawName) titleParts.push(rawName);
    if (id != null) titleParts.push(`state_id=${id}`);
    titleParts.push(`mapa=${aligned}`);
    const titleBits = titleParts.join(' — ');
    return (
      <span className={`sites-state-sema sites-state-sema--${mod}`} title={titleBits}>
        <span className="sites-state-sema__dot" aria-hidden />
        <span>{label}</span>
      </span>
    );
  };

  useEffect(() => {
    if (!token) return;
    loadSites();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSites = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { includeDevices: 'true' };
      if (search.trim()) params.search = search.trim();
      if (activeFilter !== 'all') params.active = activeFilter === 'true';

      const response = await axios.get(`${API_URL}/sites`, {
        headers: authHeaders,
        params,
      });

      setSites(response.data?.data ?? response.data ?? []);
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      setError(apiMsg || 'Error cargando sitios');
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/sites/stats`, { headers: authHeaders });
      setStats(response.data?.data ?? null);
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  };

  const resetForm = () => {
    setEditingSiteId(null);
    setForm({
      name: '',
      address: '',
      neighborhood: '',
      location: '',
      geoDepartment: '',
      geoMunicipality: '',
      latitude: '',
      longitude: '',
      ipAddress: '',
      active: true,
      certificate: false,
      stateId: '',
      clientServicesId: '',
    });
  };

  const openCamerasStream = (site: Site) => {
    const params = new URLSearchParams();
    params.set('siteId', String(site.id));
    params.set('siteName', site.name);
    navigate(`/streams?${params.toString()}`);
  };

  const openCreate = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEdit = (site: Site) => {
    // Abrir el wizard en modo edición
    setEditingSiteForWizard({
      id: site.id,
      type: site.serviceName as any || 'PRIVADO',
      name: site.name,
      address: site.address,
      neighborhood: site.neighborhood,
      location: site.location,
      geoDepartment: site.geoDepartment ?? '',
      geoMunicipality: site.geoMunicipality ?? '',
      ipAddress: site.ipAddress || '',
      latitude: site.latitude,
      longitude: site.longitude,
      active: site.active,
      certificate: site.certificate,
      streamingOnDemandEnabled: site.streamingOnDemandEnabled !== false,
    });
    setShowCreateWizard(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        name: form.name.trim(),
        address: form.address.trim(),
        active: form.active,
        certificate: form.certificate,
      };

      if (form.neighborhood.trim()) payload.neighborhood = form.neighborhood.trim();
      if (form.location.trim()) payload.location = form.location.trim();
      if (form.geoDepartment.trim()) payload.geoDepartment = form.geoDepartment.trim();
      if (form.geoMunicipality.trim()) payload.geoMunicipality = form.geoMunicipality.trim();
      if (form.latitude.trim()) payload.latitude = form.latitude.trim();
      if (form.longitude.trim()) payload.longitude = form.longitude.trim();
      if (form.ipAddress.trim()) payload.ipAddress = form.ipAddress.trim();
      if (form.stateId.trim()) payload.stateId = Number(form.stateId);
      if (form.clientServicesId.trim()) payload.clientServicesId = Number(form.clientServicesId);

      if (editingSiteId) {
        // Update: backend permite campos parciales, pero aquí enviamos el form completo
        await axios.put(`${API_URL}/sites/${editingSiteId}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API_URL}/sites`, payload, { headers: authHeaders });
      }

      await loadSites();
      loadStats();
      closeForm();
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      setError(apiMsg || 'Error guardando sitio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (site: Site) => {
    setSiteToDelete(site);
    setShowConfirmDeleteSite(true);
  };

  const confirmSiteDelete = async () => {
    if (!siteToDelete) return;
    
    setSaving(true);
    setError(null);
    try {
      await axios.delete(`${API_URL}/sites/${siteToDelete.id}`, { headers: authHeaders });
      await loadSites();
      loadStats();
      setShowConfirmDeleteSite(false);
      setSiteToDelete(null);
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      setError(apiMsg || 'Error eliminando sitio');
    } finally {
      setSaving(false);
    }
  };

  const cancelSiteDelete = () => {
    setShowConfirmDeleteSite(false);
    setSiteToDelete(null);
  };

  const siteOperationalStats = useMemo(() => deriveSitesOperationalStats(sites), [sites]);

  /** Total en catálogo (``GET /sites/stats``); fallback al listado cargado. */
  const catalogSiteTotal = stats?.sites?.total ?? siteOperationalStats.total;

  const siteOperationalAligned = (s: Site): MapAlignedOperational =>
    siteMapAlignedOperational({
      stateId: s.stateId,
      cameras: (s.devices?.cameras ?? []) as ReadonlyArray<{ state?: number | null }>,
    });

  // Filtrar por estado operativo del sitio (``state_id`` / ``sites_states``), no por ``active`` del catálogo.
  const filteredSites = useMemo(() => {
    if (cameraStatusFilter === 'online') {
      return sites.filter((s) => siteOperationalAligned(s) === 'on');
    }
    if (cameraStatusFilter === 'partial') {
      return sites.filter((s) => siteOperationalAligned(s) === 'issue');
    }
    if (cameraStatusFilter === 'offline') {
      return sites.filter((s) => {
        const aligned = siteOperationalAligned(s);
        return aligned === 'off' || aligned === 'maint' || aligned === 'unknown';
      });
    }
    return sites;
  }, [sites, cameraStatusFilter]);

  // Paginación calculada
  const totalPages = Math.ceil(filteredSites.length / PAGE_SIZE);
  const paginatedSites = filteredSites.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset página cuando cambian los sitios filtrados
  const handleLoadSitesAndResetPage = () => {
    setCurrentPage(1);
    loadSites();
    loadStats();
  };

  return (
    <div className="sites-page">
      {/* Header: Título + Botón */}
      <div className="sites-page-header">
        <div className="sites-page-header-content">
          <div>
            <h1 className="sites-page-title">Gestión de Sitios</h1>
            <p className="sites-page-subtitle">
              {catalogSiteTotal} sitios registrados
              {stats && ` (${stats.sites.private} privados, ${stats.sites.pvvc} PVVC)`}
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateWizard(true)}
            disabled={!token || saving}
            className="sites-btn-primary"
          >
            <Plus size={20} />
            Nuevo Sitio
          </button>
        </div>

        {/* Dashboard Cards: total de catálogo + semáforo operativo del listado cargado */}
        {token && (
          <div className="sites-dashboard">
            <button
              type="button"
              onClick={() => { setCameraStatusFilter('all'); setCurrentPage(1); }}
              className={`sites-stat-card ${cameraStatusFilter === 'all' ? 'sites-stat-active-red' : 'sites-stat-default-red'}`}
              title={
                stats
                  ? `Total en catálogo: ${catalogSiteTotal} (${stats.sites.active} activos, ${stats.sites.inactive} inactivos)`
                  : 'Total de sitios en el listado cargado'
              }
            >
              <div className="sites-stat-left">
                <span className="sites-stat-label">Total de sitios</span>
                <span className="sites-stat-number">{catalogSiteTotal}</span>
              </div>
              <Building2 size={24} className="sites-stat-icon sites-stat-icon-red" />
            </button>
            <button
              type="button"
              onClick={() => { setCameraStatusFilter('online'); setCurrentPage(1); }}
              className={`sites-stat-card ${cameraStatusFilter === 'online' ? 'sites-stat-active-green' : 'sites-stat-default'}`}
            >
              <div className="sites-stat-left">
                <span className="sites-stat-label">Sitios ON</span>
                <span className="sites-stat-number sites-stat-green">{siteOperationalStats.online}</span>
              </div>
              <CheckCircle size={24} className="sites-stat-icon" />
            </button>
            <button
              type="button"
              onClick={() => { setCameraStatusFilter('partial'); setCurrentPage(1); }}
              className={`sites-stat-card ${cameraStatusFilter === 'partial' ? 'sites-stat-active-orange' : 'sites-stat-default'}`}
            >
              <div className="sites-stat-left">
                <span className="sites-stat-label">Novedad</span>
                <span className="sites-stat-number sites-stat-orange">{siteOperationalStats.partial}</span>
              </div>
              <AlertCircle size={24} className="sites-stat-icon sites-stat-icon-orange" />
            </button>
            <div
              className={`sites-stat-card sites-stat-card-split ${cameraStatusFilter === 'offline' ? 'sites-stat-active-red' : 'sites-stat-default'}`}
            >
              <button
                type="button"
                className="sites-stat-card-main"
                onClick={() => { setCameraStatusFilter('offline'); setCurrentPage(1); }}
              >
                <div className="sites-stat-left">
                  <span className="sites-stat-label">Sitios OFF</span>
                  <span className="sites-stat-number sites-stat-red">{siteOperationalStats.offline}</span>
                </div>
                <WifiOff size={24} className="sites-stat-icon sites-stat-icon-red" aria-hidden />
              </button>
              <button
                type="button"
                className="sites-stat-refresh-only"
                title="Actualizar estado operativo de sitios"
                aria-label="Actualizar estado operativo de sitios"
                disabled={!token || saving}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void loadStats();
                  void loadSites();
                }}
              >
                <RefreshCw size={22} className="sites-stat-icon sites-stat-icon-red" aria-hidden />
              </button>
            </div>
          </div>
        )}

        {/* Búsqueda y Filtros */}
        <div className="sites-search-and-filters">
          <div className="sites-search-container">
            <Search className="sites-search-icon" size={20} />
            <input
              type="text"
              placeholder="Buscar por nombre, dirección o IP..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleLoadSitesAndResetPage();
              }}
              disabled={!token || saving}
              className="sites-search-input"
            />
          </div>

          <select
            className="sites-filter-select"
            value={activeFilter}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setActiveFilter(e.target.value as any)}
            disabled={!token || saving}
          >
            <option value="true">Activos en catálogo</option>
            <option value="all">Todos los sitios</option>
            <option value="false">Inactivos en catálogo</option>
          </select>

          <button
            className="sites-btn-secondary"
            onClick={handleLoadSitesAndResetPage}
            disabled={!token || saving}
          >
            Buscar
          </button>
        </div>
      </div>

      {error && <div className="sites-error">{error}</div>}

      {isFormOpen && (
        <div className="sites-form-card">
          <div className="sites-form-header">
            <h2>{editingSiteId ? `Editar sitio #${editingSiteId}` : 'Crear sitio'}</h2>
            <button className="sites-btn-secondary" onClick={closeForm} disabled={saving}>
              Cerrar
            </button>
          </div>

          <form className="sites-form" onSubmit={handleSubmit}>
            <div className="sites-form-grid">
              <label className="sites-field">
                <span>Nombre *</span>
                <input
                  className="sites-input"
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, name: e.target.value }))
                  }
                  required
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Dirección *</span>
                <input
                  className="sites-input"
                  value={form.address}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, address: e.target.value }))
                  }
                  required
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Barrio (opcional)</span>
                <input
                  className="sites-input"
                  value={form.neighborhood}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, neighborhood: e.target.value }))
                  }
                  placeholder="Ej: Centro"
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Localidad (opcional)</span>
                <input
                  className="sites-input"
                  value={form.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, location: e.target.value }))
                  }
                  placeholder="Ej: Bogotá"
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Departamento certificado (opcional)</span>
                <input
                  className="sites-input"
                  value={form.geoDepartment}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, geoDepartment: e.target.value }))
                  }
                  placeholder="PDF constancia — ej. Bogotá D.C."
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Municipio / ciudad certificado (opcional)</span>
                <input
                  className="sites-input"
                  value={form.geoMunicipality}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, geoMunicipality: e.target.value }))
                  }
                  placeholder="PDF constancia"
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>IP (opcional)</span>
                <input
                  className="sites-input"
                  value={form.ipAddress}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, ipAddress: e.target.value }))
                  }
                  placeholder="Ej: 192.168.1.10"
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>State ID (opcional)</span>
                <input
                  className="sites-input"
                  value={form.stateId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, stateId: e.target.value }))
                  }
                  placeholder="Ej: 1"
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Client Services ID (opcional)</span>
                <input
                  className="sites-input"
                  value={form.clientServicesId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, clientServicesId: e.target.value }))
                  }
                  placeholder="Ej: 10"
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Latitud (opcional)</span>
                <input
                  className="sites-input"
                  value={form.latitude}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, latitude: e.target.value }))
                  }
                  disabled={saving}
                />
              </label>

              <label className="sites-field">
                <span>Longitud (opcional)</span>
                <input
                  className="sites-input"
                  value={form.longitude}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, longitude: e.target.value }))
                  }
                  disabled={saving}
                />
              </label>
            </div>

            <div className="sites-form-row">
              <label className="sites-checkbox">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, active: e.target.checked }))
                  }
                  disabled={saving}
                />
                <span>Activo</span>
              </label>

              <label className="sites-checkbox">
                <input
                  type="checkbox"
                  checked={form.certificate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((p: SiteForm) => ({ ...p, certificate: e.target.checked }))
                  }
                  disabled={saving}
                />
                <span>Certificado</span>
              </label>

              <button className="sites-btn-primary" type="submit" disabled={saving}>
                {saving ? 'Guardando...' : editingSiteId ? 'Guardar cambios' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabla de sitios */}
      <div className="sites-content">
        <div className="sites-table-container">
          <div className="sites-table-header">
            <h2>Listado de Sitios</h2>
          </div>

          {loading ? (
            <XTAMSpinner variant="inline" message="Cargando sitios..." />
          ) : (
            <table className="sites-table">
              <thead>
                <tr>
                  <th>Sitio</th>
                  <th>Tipo</th>
                  <th>Dirección</th>
                  <th>IP Servidor</th>
                  <th>Dispositivos</th>
                  <th>Estado</th>
                  <th>Certificado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredSites.length === 0 && (
                  <tr>
                    <td colSpan={8} className="sites-empty">
                      <MapPin className="sites-empty-icon" size={48} />
                      <p>No se encontraron sitios</p>
                    </td>
                  </tr>
                )}

                {paginatedSites.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="sites-cell-with-icon">
                      <div className="sites-icon-badge">
                        {getSiteIcon(s.serviceName)}
                      </div>
                      <div>
                        <div className="sites-cell-title">{s.name}</div>
                        <div className="sites-cell-subtitle">ID: {s.id}</div>
                        {!s.active && (
                          <div className="sites-cell-subtitle sites-cell-muted">Inactivo en catálogo</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {getServiceBadge(s.serviceName)}
                  </td>
                  <td>
                    <div className="sites-cell-text">{s.address}</div>
                  </td>
                  <td>
                    <div className="sites-cell-mono">{s.ipAddress || '-'}</div>
                  </td>
                  <td>
                    {(() => {
                      const counts = getDeviceCounts(s);
                      if (counts.total === 0) {
                        return <span className="sites-devices-none">Sin dispositivos</span>;
                      }
                      return (
                        <button
                          type="button"
                          className="sites-devices-summary"
                          onClick={(e) => { e.stopPropagation(); openDevicesModal(s); }}
                          title="Ver dispositivos del sitio"
                        >
                          {counts.cameras > 0 && (
                            <span className="sites-device-chip sites-device-chip-camera">
                              <Video size={12} />
                              {counts.cameras}
                            </span>
                          )}
                          {counts.xtamVideos > 0 && (
                            <span className="sites-device-chip sites-device-chip-xtam">
                              <Server size={12} />
                              {counts.xtamVideos}
                            </span>
                          )}
                          {counts.miniots > 0 && (
                            <span className="sites-device-chip sites-device-chip-miniot">
                              <Wifi size={12} />
                              {counts.miniots}
                            </span>
                          )}
                          {counts.routers > 0 && (
                            <span className="sites-device-chip sites-device-chip-router">
                              <Router size={12} />
                              {counts.routers}
                            </span>
                          )}
                          {counts.buttons > 0 && (
                            <span className="sites-device-chip sites-device-chip-button">
                              <AlertCircle size={12} />
                              {counts.buttons}
                            </span>
                          )}
                          {counts.sirens > 0 && (
                            <span className="sites-device-chip sites-device-chip-siren">
                              <Volume2 size={12} />
                              {counts.sirens}
                            </span>
                          )}
                        </button>
                      );
                    })()}
                  </td>
                  <td>{getSiteStateBadge(s)}</td>
                  <td>
                    {s.certificate ? (
                      <span className="sites-badge sites-badge-success">Certificado</span>
                    ) : (
                      <span className="sites-badge sites-badge-inactive">Sin certificar</span>
                    )}
                  </td>
                  <td>
                    <div className="sites-actions">
                      <button
                        className="sites-action-btn"
                        onClick={() => openCamerasStream(s)}
                        disabled={saving}
                        title="Ver cámaras en stream"
                      >
                        <Camera size={16} />
                      </button>
                      <button
                        className="sites-action-btn"
                        onClick={() => openEdit(s)}
                        disabled={saving}
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="sites-action-btn sites-action-btn--danger"
                        onClick={() => handleDelete(s)}
                        disabled={saving}
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="sites-pagination">
              <div className="sites-pagination-info">
                Mostrando {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, filteredSites.length)} de {filteredSites.length} sitios
              </div>
              <div className="sites-pagination-controls">
                <button
                  className="sites-pagination-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="Primera página"
                >
                  «
                </button>
                <button
                  className="sites-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .reduce<(number | string)[]>((acc, page, idx, arr) => {
                    if (idx > 0 && typeof arr[idx - 1] === 'number' && (page as number) - (arr[idx - 1] as number) > 1) {
                      acc.push('...');
                    }
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '...' ? (
                      <span key={`dots-${idx}`} className="sites-pagination-dots">...</span>
                    ) : (
                      <button
                        key={item}
                        className={`sites-pagination-btn ${currentPage === item ? 'active' : ''}`}
                        onClick={() => setCurrentPage(item as number)}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  className="sites-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className="sites-pagination-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Última página"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Site Wizard */}
      <CreateSiteWizard
        isOpen={showCreateWizard}
        onClose={() => {
          setShowCreateWizard(false);
          setEditingSiteForWizard(null);
        }}
        onSuccess={() => {
          loadSites();
          loadStats();
          setEditingSiteForWizard(null);
        }}
        token={token || ''}
        editingSite={editingSiteForWizard}
      />

      {/* Modal de confirmación para eliminar sitio */}
      <ConfirmDialog
        isOpen={showConfirmDeleteSite}
        title="¿Eliminar sitio?"
        message={siteToDelete ? `Esta acción eliminará permanentemente el sitio "${siteToDelete.name}". Esta acción no se puede deshacer.` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={confirmSiteDelete}
        onCancel={cancelSiteDelete}
        loading={saving}
      />

      {/* Modal de dispositivos del sitio */}
      {showDevicesModal && devicesModalSite && (
        <div className="sites-devices-modal-overlay" onClick={() => setShowDevicesModal(false)}>
          <div className="sites-devices-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sites-devices-modal-header">
              <div>
                <h2 className="sites-devices-modal-title">
                  <Monitor size={20} />
                  Dispositivos de {devicesModalSite.name}
                </h2>
                <p className="sites-devices-modal-subtitle">
                  {getDeviceCounts(devicesModalSite).total} dispositivos asignados
                </p>
              </div>
              <button className="sites-devices-modal-close" onClick={() => setShowDevicesModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="sites-devices-modal-content">
              {/* Cámaras */}
              {(devicesModalSite.devices?.cameras?.length || 0) > 0 && (
                <div className="sites-devices-modal-section">
                  <div className="sites-devices-modal-section-header">
                    <Camera size={16} />
                    <h3>Cámaras ({devicesModalSite.devices!.cameras!.length})</h3>
                  </div>
                  <div className="sites-devices-modal-list">
                    {devicesModalSite.devices!.cameras!.map((cam: any) => (
                      <div key={cam.id} className="sites-devices-modal-item">
                        <div className="sites-devices-modal-item-info">
                          <span className="sites-devices-modal-item-name">{cam.name || 'Sin nombre'}</span>
                          <span className="sites-devices-modal-item-detail">
                            {cam.inventory_cameras_id ? `Inv #${cam.inventory_cameras_id}` : ''}
                            {cam.url_rtsp_out ? ` • RTSP` : ''}
                            {cam.is_onvif ? ' • ONVIF' : ''}
                          </span>
                        </div>
                        {cam.ipaddress && (
                          <button
                            type="button"
                            className={`sites-devices-modal-item-ip ${copiedIp === cam.ipaddress ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(cam.ipaddress)}
                            title="Copiar IP"
                          >
                            {cam.ipaddress}
                            {copiedIp === cam.ipaddress ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* XTAM Videos */}
              {(devicesModalSite.devices?.xtamVideos?.length || 0) > 0 && (
                <div className="sites-devices-modal-section">
                  <div className="sites-devices-modal-section-header">
                    <Server size={16} />
                    <h3>XTAM Video ({devicesModalSite.devices!.xtamVideos!.length})</h3>
                  </div>
                  <div className="sites-devices-modal-list">
                    {devicesModalSite.devices!.xtamVideos!.map((xtam: any) => (
                      <div key={xtam.id} className="sites-devices-modal-item">
                        <div className="sites-devices-modal-item-info">
                          <span className="sites-devices-modal-item-name">
                            {xtam.inventory?.serialDevice || `XTAM #${xtam.id}`}
                          </span>
                          <span className="sites-devices-modal-item-detail">
                            {xtam.inventory?.modelName || ''}
                            {xtam.inventory?.mac ? ` • MAC: ${xtam.inventory.mac}` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MinIoT */}
              {(devicesModalSite.devices?.miniots?.length || 0) > 0 && (
                <div className="sites-devices-modal-section">
                  <div className="sites-devices-modal-section-header">
                    <Wifi size={16} />
                    <h3>MinIoT ({devicesModalSite.devices!.miniots!.length})</h3>
                  </div>
                  <div className="sites-devices-modal-list">
                    {devicesModalSite.devices!.miniots!.map((miniot: any) => (
                      <div key={miniot.id} className="sites-devices-modal-item">
                        <div className="sites-devices-modal-item-info">
                          <span className="sites-devices-modal-item-name">
                            {miniot.inventory?.serialDevice || `MinIoT #${miniot.id}`}
                          </span>
                          <span className="sites-devices-modal-item-detail">
                            {miniot.inventory?.modelName || ''}
                            {miniot.inventory?.mac ? ` • MAC: ${miniot.inventory.mac}` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Routers */}
              {(devicesModalSite.devices?.routers?.length || 0) > 0 && (
                <div className="sites-devices-modal-section">
                  <div className="sites-devices-modal-section-header">
                    <Router size={16} />
                    <h3>Routers ({devicesModalSite.devices!.routers!.length})</h3>
                  </div>
                  <div className="sites-devices-modal-list">
                    {devicesModalSite.devices!.routers!.map((router: any) => (
                      <div key={router.id} className="sites-devices-modal-item">
                        <div className="sites-devices-modal-item-info">
                          <span className="sites-devices-modal-item-name">
                            {router.inventory?.serialDevice || `Router #${router.id}`}
                          </span>
                          <span className="sites-devices-modal-item-detail">
                            {router.inventory?.modelName || ''}
                            {router.inventory?.mac ? ` • MAC: ${router.inventory.mac}` : ''}
                          </span>
                        </div>
                        {router.ipaddress && (
                          <button
                            type="button"
                            className={`sites-devices-modal-item-ip ${copiedIp === router.ipaddress ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(router.ipaddress)}
                            title="Copiar IP"
                          >
                            {router.ipaddress}
                            {copiedIp === router.ipaddress ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botones */}
              {(devicesModalSite.devices?.buttons?.length || 0) > 0 && (
                <div className="sites-devices-modal-section">
                  <div className="sites-devices-modal-section-header">
                    <AlertCircle size={16} />
                    <h3>Botones de Pánico ({devicesModalSite.devices!.buttons!.length})</h3>
                  </div>
                  <div className="sites-devices-modal-list">
                    {devicesModalSite.devices!.buttons!.map((button: any) => (
                      <div key={button.id} className="sites-devices-modal-item">
                        <div className="sites-devices-modal-item-info">
                          <span className="sites-devices-modal-item-name">
                            {button.inventory?.serialDevice || `Botón #${button.id}`}
                          </span>
                          <span className="sites-devices-modal-item-detail">
                            {button.identificationButton ? `ID: ${button.identificationButton}` : ''}
                            {button.inventory?.mac ? ` • MAC: ${button.inventory.mac}` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sirenas */}
              {(devicesModalSite.devices?.sirens?.length || 0) > 0 && (
                <div className="sites-devices-modal-section">
                  <div className="sites-devices-modal-section-header">
                    <Volume2 size={16} />
                    <h3>Sirenas ({devicesModalSite.devices!.sirens!.length})</h3>
                  </div>
                  <div className="sites-devices-modal-list">
                    {devicesModalSite.devices!.sirens!.map((siren: any) => (
                      <div key={siren.id} className="sites-devices-modal-item">
                        <div className="sites-devices-modal-item-info">
                          <span className="sites-devices-modal-item-name">
                            {siren.inventory?.serialDevice || `Sirena #${siren.id}`}
                          </span>
                          <span className="sites-devices-modal-item-detail">
                            {siren.inventory?.mac ? `MAC: ${siren.inventory.mac}` : ''}
                          </span>
                        </div>
                        {siren.ipaddress && (
                          <button
                            type="button"
                            className={`sites-devices-modal-item-ip ${copiedIp === siren.ipaddress ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(siren.ipaddress)}
                            title="Copiar IP"
                          >
                            {siren.ipaddress}
                            {copiedIp === siren.ipaddress ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {getDeviceCounts(devicesModalSite).total === 0 && (
                <div className="sites-devices-modal-empty">
                  <Monitor size={48} />
                  <p>No hay dispositivos asignados a este sitio</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast de IP copiada */}
      {copiedIp && (
        <div className="sites-ip-copied-toast">
          <Check size={14} />
          IP {copiedIp} copiada al portapapeles
        </div>
      )}
    </div>
  );
};

export default SitesPage;
